import { createSelector } from 'reselect'
import moment from 'moment'
import { findPostPurchaseCheckout, isLEHotelItem, isNoProtectionInsuranceItem } from 'lib/checkout/checkoutUtils'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import { getAllOffers } from 'selectors/offerSelectors'
import { selectIsGuestCheckoutMode } from 'selectors/checkoutSelectors'
import { checkoutAccommodationOfferView } from 'checkout/selectors/view/accommodation'
import { isSpoofed } from 'selectors/isSpoofed'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { getOptimizelyFeatureFlagVariation } from 'lib/optimizely/optimizelyUtils'
import { getCartHasPropertyCollectItem } from '../propertyCollectSelectors'

const isGroupPaymentEligible = createSelector(
  (state: App.State) => getOptimizelyFeatureFlagVariation(
    state,
    OptimizelyFeatureFlags.paymentsGroupPayments,
  ),
  isSpoofed,
  selectIsGuestCheckoutMode,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.cart.isGift,
  getCartHasPropertyCollectItem,
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getAllOffers(state),
  (state: App.State) => checkoutAccommodationOfferView(state),
  (
    enabled,
    spoofed,
    isGuestCheckoutMode,
    postPurchase,
    isGift,
    hasPropertyCollectItem,
    cartItems,
    offers,
    accommodationOfferView,
  ): boolean => {
    if (!(spoofed || enabled) || isGuestCheckoutMode || postPurchase || isGift || hasPropertyCollectItem) {
      return false
    }

    const payableItems = cartItems.filter(item => !isNoProtectionInsuranceItem(item))
    if (!payableItems.length || !payableItems.every(isLEHotelItem)) {
      return false
    }

    const offerIds = new Set(payableItems.map(item => item.offerId))
    if (offerIds.size !== 1) {
      return false
    }

    const offer = offers[payableItems[0].offerId]
    if (!isFlashHotelOffer(offer)) {
      return false
    }

    return accommodationOfferView.hasRequiredData &&
      accommodationOfferView.data.length > 0 &&
      accommodationOfferView.data.every(view => {
        const checkIn = view.startDate ? moment(view.startDate) : undefined
        return view.itemViews.length > 0 && view.itemViews.every(itemView => {
          const pkg = 'pkg' in itemView ? itemView.pkg : undefined
          return isPackageEligibleForZeroReservation(
            offer,
            checkIn,
            pkg?.roomRate?.cancellationPolicy?.type,
          )
        })
      })
  },
)

export default isGroupPaymentEligible
